import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

interface LoaderProps {
  size?: 'small' | 'default' | 'large';
  variant?: 'fullscreen' | 'inline' | 'overlay';
  message?: string;
  showMessage?: boolean;
  className?: string;
}

const Loader: React.FC<LoaderProps> = ({
  size = 'large',
  variant = 'fullscreen',
  message = 'Loading...',
  showMessage = true,
  className = '',
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return { fontSize: 24, messageSize: 'text-sm' };
      case 'large':
        return { fontSize: 60, messageSize: 'text-lg' };
      default:
        return { fontSize: 40, messageSize: 'text-base' };
    }
  };

  const { fontSize, messageSize } = getSizeConfig();

  const getVariantStyles = () => {
    switch (variant) {
      case 'inline':
        return {
          container: 'flex items-center justify-center p-4',
          overlay: false,
        };
      case 'overlay':
        return {
          container: 'absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm rounded-lg z-10',
          overlay: false,
        };
      default: // fullscreen
        return {
          container: 'fixed inset-0 flex items-center justify-center z-9999 bg-black bg-opacity-30 backdrop-blur-sm',
          overlay: true,
        };
    }
  };

  const { container, overlay } = getVariantStyles();

  const spinnerElement = (
    <div
      className={`flex flex-col items-center justify-center space-y-4 ${className}`}
      role="status"
      aria-label="Loading content"
    >
      <div className="relative">
        {/* Main spinner */}
        <Spin
          indicator={
            <LoadingOutlined
              style={{
                fontSize,
                color: '#31347c', // Using primary color from theme
              }}
              spin
            />
          }
        />
        {/* Subtle pulsing ring */}
        <div
          className="absolute inset-0 rounded-full border-2 border-primary border-opacity-20 animate-ping"
          style={{
            width: fontSize + 20,
            height: fontSize + 20,
            marginTop: -10,
            marginLeft: -10,
          }}
          aria-hidden="true"
        />
      </div>

      {showMessage && message && <div className={`${messageSize} font-medium text-primary animate-pulse`}>{message}</div>}

      {/* Loading dots animation */}
      {showMessage && (
        <div className="flex space-x-1" aria-hidden="true">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      )}
    </div>
  );

  if (variant === 'fullscreen' && overlay) {
    return <div className={container}>{spinnerElement}</div>;
  }

  return <div className={container}>{spinnerElement}</div>;
};

export default Loader;
